import React, { useState, useEffect } from "react";
import "./styles.css";
import {
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeHeader from "../../components/HomeHeader";

export default function NewJobPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  // Retrieve the token from localStorage on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setNotification({
        open: true,
        message: "Authentication required. Please log in.",
        severity: "error"
      });
      navigate("/login");
    } else {
      console.log("Retrieved token:", storedToken);
      setToken(storedToken);
    }
  }, [navigate]);

  // State for job details with explicit title and description properties
  const [jobDetails, setJobDetails] = useState({
    title: "",
    description: "",
    jobCategory: "",
    jobType: "",
    jobVisitDate: "",
    jobExecutionDate: "",
    priority: "NEW",
    location: "",
    postedBy: "admin",
    requirements: "",
    salary: "",
    isEmergency: false,
    recurringIssue: false,
    scheduledTime: "",
  });

  // Handle error notifications
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobDetails((prevDetails) => ({
      ...prevDetails,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      
      // Create a preview of the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload the selected image using the correct endpoint
  const uploadImage = async (jobId) => {
    if (!selectedImage) return null;

    console.log("Preparing to upload image for job ID:", jobId);
    console.log("Image details:", selectedImage.name, selectedImage.type, selectedImage.size);

    const formData = new FormData();
    // Changed from 'file' to 'files' to match the backend parameter name
    formData.append('files', selectedImage);

    try {
      console.log("Sending image upload request...");
      // Using the correct endpoint format as specified
      const response = await fetch(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Note: Don't set Content-Type header when sending FormData
        },
        body: formData,
      });

      console.log("Image upload response status:", response.status, response.statusText);
      
      // Check if the response is ok before trying to process it
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Image upload error response:', errorText);
        throw new Error(`Failed to upload image: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Check content type before trying to parse JSON
      const contentType = response.headers.get("content-type");
      console.log("Response content type:", contentType);
      
      if (contentType && contentType.includes("application/json")) {
        try {
          const result = await response.json();
          console.log('Image uploaded successfully with JSON response:', result);
          return result.imagePath || true; // Return image path if available
        } catch (jsonError) {
          console.error("Error parsing JSON response:", jsonError);
          // If JSON parsing fails, try to get the text response
          const textResponse = await response.text();
          console.log("Response as text:", textResponse);
          return true; // Consider the upload successful even if parsing failed
        }
      } else {
        // Handle non-JSON responses
        const textResult = await response.text();
        console.log('Image uploaded with text response:', textResult);
        return true; // Consider the upload successful
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setNotification({
        open: true,
        message: 'Failed to upload image, but job was created',
        severity: "warning"
      });
      return null;
    }
  };

  // Validate form fields
  const validateForm = () => {
    if (!jobDetails.title.trim()) {
      setNotification({
        open: true,
        message: "Job title cannot be empty",
        severity: "error"
      });
      return false;
    }

    if (!jobDetails.description.trim()) {
      setNotification({
        open: true,
        message: "Job description cannot be empty",
        severity: "error"
      });
      return false;
    }

    if (!jobDetails.jobCategory) {
      setNotification({
        open: true,
        message: "Please select a job category",
        severity: "error"
      });
      return false;
    }

    if (!jobDetails.jobType) {
      setNotification({
        open: true,
        message: "Please select a job type",
        severity: "error"
      });
      return false;
    }

    if (!jobDetails.jobExecutionDate) {
      setNotification({
        open: true,
        message: "Job execution date is required",
        severity: "error"
      });
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const formattedJobDetails = {
      ...jobDetails,
      title: jobDetails.title.trim(),
      description: jobDetails.description.trim(),
      jobVisitDate: jobDetails.jobVisitDate ? new Date(jobDetails.jobVisitDate).toISOString() : null,
      jobExecutionDate: jobDetails.jobExecutionDate ? new Date(jobDetails.jobExecutionDate).toISOString() : null,
      scheduledTime: jobDetails.scheduledTime ? new Date(jobDetails.scheduledTime).toISOString() : null,
    };

    console.log("Sending job details:", formattedJobDetails);

    try {
      console.log("Making job creation request...");
      const response = await fetch("https://jobmanagement-497524552274.us-central1.run.app/api/jobs/create", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedJobDetails),
      });

      console.log("Job creation response status:", response.status, response.statusText);
      
      // First try to get the response text
      const responseText = await response.text();
      console.log("Job creation raw response:", responseText);
      
      let responseData;
      
      // Then try to parse it as JSON
      try {
        responseData = responseText ? JSON.parse(responseText) : {};
        console.log("Parsed response data:", responseData);
      } catch (e) {
        console.error("Invalid JSON response:", responseText);
        responseData = { message: responseText || "Unknown error" };
      }

      if (response.ok) {
        console.log("Job created successfully:", responseData);
        
        // Get the job ID from the response
        const jobId = responseData.id || responseData.jobId;
        console.log("Extracted job ID:", jobId);
        
        // If there's an image and we have a job ID, upload it
        let imagePath = null;
        if (selectedImage && jobId) {
          console.log("Attempting to upload image for job ID:", jobId);
          imagePath = await uploadImage(jobId);
          console.log("Image upload result:", imagePath);
        } else {
          console.log("No image to upload or no job ID available");
        }
        
        setNotification({
          open: true,
          message: `Job created successfully!${imagePath ? ' Image uploaded.' : ''}`,
          severity: "success"
        });
        
        // Reset form or navigate
        setTimeout(() => {
          navigate("/Job");
        }, 1500);
      } else if (response.status === 403) {
        let errorMessage = "You do not have permission to create a job.";
        
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        }
        
        setNotification({
          open: true,
          message: errorMessage,
          severity: "error"
        });
        
        if (errorMessage === "You do not have permission to create a job.") {
          setTimeout(() => {
            navigate("/login");
          }, 1500);
        }
      } else {
        let errorMessage = "An error occurred while creating the job.";
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        }
        
        setNotification({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error creating job:", error);
      setNotification({
        open: true,
        message: "Network error. Please check your connection and try again.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="newJobPage">
      <HomeHeader header="Report a Problem" />
      <Grid>
        <Typography
          style={{
            fontSize: "15px",
            fontWeight: "medium",
            letterSpacing: "0.09px",
            paddingTop: "25px",
            paddingLeft: "2px",
            paddingBottom: "20px",
          }}
        >
          Report a new problem, such as a burst pipe, and request a plumber.
        </Typography>
      </Grid>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Title"
              variant="outlined"
              name="title"
              value={jobDetails.title}
              onChange={handleChange}
              required
              error={jobDetails.title === ""}
              helperText={jobDetails.title === "" ? "Title is required" : ""}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Description"
              variant="outlined"
              name="description"
              value={jobDetails.description}
              onChange={handleChange}
              required
              multiline
              rows={4}
              error={jobDetails.description === ""}
              helperText={jobDetails.description === "" ? "Description is required" : ""}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Execution Date"
              type="datetime-local"
              variant="outlined"
              name="jobExecutionDate"
              value={jobDetails.jobExecutionDate}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              required
              error={jobDetails.jobExecutionDate === ""}
              helperText={jobDetails.jobExecutionDate === "" ? "Execution date is required" : ""}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" required error={jobDetails.jobCategory === ""}>
              <InputLabel>Job Category</InputLabel>
              <Select
                name="jobCategory"
                value={jobDetails.jobCategory}
                onChange={handleChange}
                label="Job Category"
              >
                <MenuItem value="Water Tank and Motor repair">
                  Water Tank and Motor repair
                </MenuItem>
                <MenuItem value="Plumbing">Plumbing</MenuItem>
              </Select>
              {jobDetails.jobCategory === "" && (
                <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '4px' }}>
                  Category is required
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" required error={jobDetails.jobType === ""}>
              <InputLabel>Job Type</InputLabel>
              <Select
                name="jobType"
                value={jobDetails.jobType}
                onChange={handleChange}
                label="Job Type"
              >
                <MenuItem value="Full-Time">Full-Time</MenuItem>
                <MenuItem value="Part-Time">Part-Time</MenuItem>
              </Select>
              {jobDetails.jobType === "" && (
                <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '4px' }}>
                  Job type is required
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location"
              variant="outlined"
              name="location"
              value={jobDetails.location}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography component="div" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="isEmergency"
                  checked={jobDetails.isEmergency}
                  onChange={handleChange}
                />
                Is this an emergency?
              </label>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography component="div" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="recurringIssue"
                  checked={jobDetails.recurringIssue}
                  onChange={handleChange}
                />
                Is this a recurring issue?
              </label>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Latest Scheduled Date"
              type="datetime-local"
              variant="outlined"
              name="scheduledTime"
              value={jobDetails.scheduledTime}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="image-upload"
            />
            <label htmlFor="image-upload">
              <Button
                variant="contained"
                component="span"
                color="primary"
              >
                Upload Image
              </Button>
            </label>
            {imagePreview && (
              <div>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: '150px', height: '150px', marginTop: '10px', objectFit: 'cover', borderRadius: '4px' }}
                />
                <Typography variant="caption" display="block" style={{ marginTop: '4px' }}>
                  {selectedImage ? selectedImage.name : ""}
                </Typography>
              </div>
            )}
          </Grid>

          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Create Job"}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
}